import { Box, Typography, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import { Star } from 'lucide-react';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import _ from 'underscore';
import { createFuzzyFilterOptions } from 'utils/fuzzy-filter';
import { EditOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/edit-order-form-values.type';
import { getOrderDateParts } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { isDateWithinProductRange } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { CustomerAssortment } from 'views/order-feed/types/customer-assortment.type';

type EditProductAutocompleteProps = {
  idx: number;
  products: CustomerAssortment | undefined;
  disabled?: boolean;
};

const EditProductAutocomplete: React.FC<EditProductAutocompleteProps> = ({ idx, products, disabled }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { control } = useFormContext<EditOrderFormValues>();
  const watchOrderLines = useWatch({ control, name: 'orderLines' });
  const watchRequestedDispatchDate = useWatch({ control, name: 'requestedDispatchDate' });

  const productOptions = useMemo(() => {
    const availableProducts = new Map(
      products?.availableProducts?.map((product) => [product.itemNumber, { ...product, preferred: false }])
    );
    const preferredProducts = new Map(
      products?.preferredProducts?.map((product) => [product.itemNumber, { ...product, preferred: true }])
    );
    const combined = new Map([...availableProducts, ...preferredProducts]);

    return _.chain(Array.from(combined.values())).sortBy('itemName').reverse().sortBy('preferred').reverse().value();
  }, [products]);

  const productOptionsByItemNumber = useMemo(() => _.indexBy(productOptions, 'itemNumber'), [productOptions]);
  const itemNumberOptions = useMemo(() => productOptions.map((product) => product.itemNumber), [productOptions]);
  const requestedDispatchDate = getOrderDateParts(watchRequestedDispatchDate || '').requestedDispatchDate;
  const preferredProductsLabel = intl.formatMessage({
    id: 'orderFeed.listOrderSupplyPlan.orderList.createOrder.preferredProductsGroup',
  });
  const allProductsLabel = intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.createOrder.availableProductsGroup' });

  const getProductLabel = (itemNumber: string): string => {
    const product = productOptionsByItemNumber[itemNumber];
    return product?.itemName ?? itemNumber;
  };

  const filterOptions = useMemo(
    () =>
      createFuzzyFilterOptions<string>((itemNumber) => {
        const product = productOptionsByItemNumber[itemNumber];
        return [product?.itemName, itemNumber].filter(Boolean).join(' ');
      }),
    [productOptionsByItemNumber]
  );

  return (
    <Autocomplete<string>
      dark
      fullWidth
      openOnFocus
      label={intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.productLabel' })}
      disableCloseOnSelect={false}
      disabled={disabled}
      options={itemNumberOptions}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={getProductLabel}
      filterOptions={filterOptions}
      getOptionDisabled={(option) => watchOrderLines?.some((line, lineIdx) => lineIdx !== idx && line.itemNumber === option) ?? false}
      groupBy={(option) => (productOptionsByItemNumber[option]?.preferred ? preferredProductsLabel : allProductsLabel)}
      renderOption={(props, option) => {
        const product = productOptionsByItemNumber[option];
        const isSelected = option === watchOrderLines?.[idx]?.itemNumber;
        const preferredStarFill = isSelected ? theme.palette.darkBlue.main : theme.palette.common.white;

        return (
          <Box {...props} key={option} component="li" display="flex" alignItems="center" gap={2}>
            {product?.preferred && <Star size={20} fill={preferredStarFill} style={{ flexShrink: 0 }} />}
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" color="darkBlue.main">
                {product.itemName}
              </Typography>
              <Typography fontSize={14} variant="caption" color="darkBlue.o50">
                {Number(option)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      controller={{
        name: `orderLines.${idx}.itemNumber`,
        control,
        rules: {
          required: intl.formatMessage({ id: 'form.rules.required' }),
          validate: {
            valid: (itemNumber: string) => isDateWithinProductRange(productOptions, itemNumber, requestedDispatchDate),
          },
        },
      }}
    />
  );
};

export default EditProductAutocomplete;